import { useContext } from 'react';
import InvoicesToPayContext from '../contexts/InvoicesToPayContext';
import MerchantContext from '../contexts/MerchantContext';
import SubscriptionsContext from '../contexts/SubscriptionsContext';
import InvoiceDataContext from '../contexts/InvoiceDataContext';
import { useTRPC } from '../trpc/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { inferProcedureInput } from '@trpc/server';
import type { invoicesRouter } from '@src/backend/src/rentals/functions/paymentsPortal/invoices';
import AuthContext from '../contexts/AuthContext';
import PaymentModalStatusContext from '../contexts/PaymentModalStatusContext';
import ChallengeModalContext from '../contexts/ChallengeModalContext';
import { getCreditsToRedeem } from '../helpers/credits';
import type { Captcha } from './useCaptcha';

// The captcha comes from the caller rather than from a `useCaptcha` call here:
// the widget has to live in the caller's layout, and a second call would render
// a second widget.
export const usePayment = (captcha: Captcha, onError?: () => void) => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const payMutation = useMutation({
    ...trpc.invoices.pay.mutationOptions(),
  });

  const { merchant } = useContext(MerchantContext);
  const invoicesToPayCtx = useContext(InvoicesToPayContext);
  const { documentNumber } = useContext(AuthContext);
  const { setInvoiceIds: setPaymentModalInvoiceIds } = useContext(PaymentModalStatusContext);
  const { openChallengeModal } = useContext(ChallengeModalContext);
  const { subscriptionToCharge, refetchActive } = useContext(SubscriptionsContext);
  const { creditsBalance, useCredits } = useContext(InvoiceDataContext);

  const processPayment = async (
    payObject: inferProcedureInput<typeof invoicesRouter.pay>['payment'],
    payerInfo: inferProcedureInput<typeof invoicesRouter.pay>['payerInfo'],
    source: 'portal' | 'link' | 'whatsapp',
    puntosColombia?: inferProcedureInput<typeof invoicesRouter.pay>['puntosColombia']
  ) => {
    if (invoicesToPayCtx.invoicesToPay.length < 1) return;
    if (!merchant?.id) return;

    if (!documentNumber) {
      console.error('rentalsPaymentsPortal usePayment processPayment missing customer document number');
      onError?.();
      return;
    }

    // The button that calls this is disabled without a token, so reaching here
    // without one means the widget lost it between render and click.
    const captchaToken = captcha.token;
    if (!captchaToken) {
      console.error('rentalsPaymentsPortal usePayment processPayment missing captcha token');
      onError?.();
      return;
    }

    invoicesToPayCtx.setInvoiceSelectionLocked(true);

    const baseTotal = invoicesToPayCtx.totalAmountToPay + (subscriptionToCharge?.price ?? 0);
    const creditsApplied = useCredits
      ? getCreditsToRedeem({ baseTotal, requestedCredits: creditsBalance, creditsBalance })
      : 0;

    try {
      const result = await payMutation.mutateAsync({
        rentalsMerchantId: merchant.id,
        customerDocumentNumber: documentNumber,
        captchaToken,
        payerInfo,
        invoices: invoicesToPayCtx.invoicesToPay.map((i) => ({ ...i, amount: i.partialAmount })),
        payment: payObject,
        subscription: subscriptionToCharge ?? undefined,
        source,
        puntosColombia,
        credits: creditsApplied > 0 ? { amount: creditsApplied } : undefined,
      });

      if (result?.[0]?.paymentUrl && (payObject.type === 'pse' || payObject.type === 'bancolombiaButton')) {
        window.location.replace(result[0]?.paymentUrl);
        return;
      }
      if (result?.[0]?.challenge) await openChallengeModal({ challenge: result[0].challenge });

      const paidInvoiceIds = invoicesToPayCtx.invoicesToPay.map((invoice) => invoice.id);
      invoicesToPayCtx.clearInvoicesToPay();
      refetchActive();

      // Public push already replaces checkout with the full-page receipt once
      // the invoice has a payment outcome. Opening the portal modal there
      // stacks two receipts for in-page methods (card, Nequi).
      if (source === 'link') {
        for (const id of paidInvoiceIds) {
          void queryClient.invalidateQueries({
            queryKey: trpc.invoices.getApiInvoice.queryKey({
              rentalsMerchantId: merchant.id,
              id,
            }),
          });
        }
        return;
      }

      setPaymentModalInvoiceIds(paidInvoiceIds);
    } catch (error) {
      console.error('Rentals dashboard usePayment processPayment error', error);
      onError?.();
    } finally {
      // The token is spent either way, so a retry needs a fresh one.
      captcha.reset();
      invoicesToPayCtx.setInvoiceSelectionLocked(false);
    }
  };

  return { processPayment, loading: payMutation.isPending };
};
